import { DEFAULT_DRAWER_REAR_CLEARANCE, MAX_DRAWERS, type AssemblyInstance, type DrawerSet, type MaterialSlot } from '@scs/domain-engine';
import { IntInput, MmInput } from '../lib/ui';
import type { EditorStore } from './store';

type Exec = EditorStore['exec'];

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="prop-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

const SLOT_LABEL: Record<MaterialSlot, string> = {
  'drawer-bottom': 'Dno ladice (kao leđa)',
  back: 'Leđa',
  carcass: 'Korpus',
  'drawer-box': 'Kutija ladice',
  front: 'Fronte',
  plinth: 'Sokla',
};

/** Drawer set ("Ladice"): count, slide / top / bottom clearance, box and bottom boards, bottom material. */
export function DrawerProperties({ inst, set, index, exec, disabled, onRemoved }: { inst: AssemblyInstance; set: DrawerSet; index: number | null; exec: Exec; disabled: boolean; onRemoved(): void }) {
  const lock = disabled || inst.locked;
  const edit = (label: string, fn: (s: DrawerSet) => void) =>
    exec(label, (d) => {
      const s = d.assemblies.find((a) => a.id === inst.id)?.drawerSets?.find((x) => x.id === set.id);
      if (s) fn(s);
    });
  const shortened = Object.entries(set.setbacks ?? {}).filter(([k, v]) => v > 0 && Number(k) <= set.count);
  return (
    <div className="prop-group drawer-group">
      <h3>
        ▥ {set.label}
        {index !== null && <span className="faint"> · ladica {index}</span>}
      </h3>
      <Row label="Naziv">
        <input
          className="input"
          style={{ height: 26 }}
          defaultValue={set.label}
          key={set.label}
          disabled={lock}
          onKeyDown={(e) => e.stopPropagation()}
          onBlur={(e) => e.target.value.trim() && e.target.value !== set.label && edit('Naziv ladica', (s) => (s.label = e.target.value.trim().slice(0, 200)))}
        />
      </Row>
      <Row label="Broj ladica">
        <IntInput label="Broj ladica" value={set.count} min={1} max={MAX_DRAWERS} disabled={lock} onCommit={(v) => edit('Broj ladica', (s) => (s.count = v))} />
      </Row>
      <Row label="Pregrade">
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            aria-label="Pregrade između ladica"
            disabled={lock || set.count < 2}
            checked={!!set.dividerThickness}
            onChange={(e) =>
              edit(e.target.checked ? 'Pregrade između ladica' : 'Bez pregrada između ladica', (s) => {
                if (e.target.checked) s.dividerThickness = 18000;
                else delete s.dividerThickness;
              })
            }
          />
          {set.dividerThickness ? (
            <MmInput label="Debljina pregrada" value={set.dividerThickness} min={1} max={50} disabled={lock} onCommit={(v) => edit('Debljina pregrada ladica', (s) => (s.dividerThickness = v))} />
          ) : (
            <span className="faint">između ladica</span>
          )}
        </span>
      </Row>
      <Row label="Klizači (bok)">
        <MmInput label="Prostor za klizače" value={set.slideClearance} min={0} max={100} disabled={lock} onCommit={(v) => edit('Prostor za klizače', (s) => (s.slideClearance = v))} />
      </Row>
      <Row label="Zazor gore">
        <MmInput label="Gornji zazor" value={set.topClearance} min={0} max={200} disabled={lock} onCommit={(v) => edit('Gornji zazor ladica', (s) => (s.topClearance = v))} />
      </Row>
      <Row label="Zazor dolje">
        <MmInput label="Donji zazor" value={set.bottomClearance} min={0} max={200} disabled={lock} onCommit={(v) => edit('Donji zazor ladica', (s) => (s.bottomClearance = v))} />
      </Row>
      <Row label="Zazor od leđa">
        <MmInput label="Zazor od leđa" value={set.rearClearance ?? DEFAULT_DRAWER_REAR_CLEARANCE} min={0} max={200} disabled={lock} onCommit={(v) => edit('Zazor ladica od leđa', (s) => (s.rearClearance = v))} />
      </Row>
      <Row label="Debljina kutije">
        <MmInput label="Debljina kutije" value={set.boxThickness} min={1} max={50} disabled={lock} onCommit={(v) => edit('Debljina kutije ladica', (s) => (s.boxThickness = v))} />
      </Row>
      <Row label="Dno: materijal">
        <select className="input" disabled={lock} value={set.bottomSlot} onChange={(e) => edit('Materijal dna ladica', (s) => (s.bottomSlot = e.target.value as MaterialSlot))}>
          {(Object.keys(SLOT_LABEL) as MaterialSlot[]).map((k) => (
            <option key={k} value={k}>
              {SLOT_LABEL[k]}
            </option>
          ))}
        </select>
      </Row>
      <Row label="Dno: debljina">
        <MmInput label="Debljina dna" value={set.bottomThickness} min={1} max={50} disabled={lock} onCommit={(v) => edit('Debljina dna ladica', (s) => (s.bottomThickness = v))} />
      </Row>
      <p className="faint" style={{ fontSize: 11, margin: '4px 0' }}>
        Kutija: dva duža boka, dvije kraće ploče između njih, dno odozdo. Dekor materijala se bira u „Podrazumijevani materijali”.
        {shortened.length > 0 && ` Skraćeno zbog unutarnje fronte: ${shortened.map(([k, v]) => `ladica ${k} (${v / 1000} mm)`).join(', ')}.`}
      </p>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button
          className="btn sm ghost danger"
          disabled={lock}
          onClick={() => {
            exec('Obriši ladice', (d) => {
              const a = d.assemblies.find((x) => x.id === inst.id);
              if (a) a.drawerSets = (a.drawerSets ?? []).filter((s) => s.id !== set.id);
            });
            onRemoved();
          }}
        >
          Obriši ladice
        </button>
      </div>
    </div>
  );
}
